import { db } from "./db";
import { remoteUrl } from "./remoteConfig";
import { defaultReaderSettings, type ReaderSettings } from "@/types/reader";
import { defaultTTSSettings, type TTSSettings } from "@/types/tts";

/** localStorage marker for the one-time dark-default migration of the
 *  Dexie-stored reader settings.
 *
 *  DELIBERATELY a different key from the one index.html's pre-paint script
 *  uses. They migrate two different stores (that script only touches the
 *  `pv-theme` pre-paint hint; this touches the authoritative settings row),
 *  and sharing a key meant the inline script — which runs first — set the
 *  marker and this migration then skipped, leaving the stored theme on
 *  "paper" forever. Observed exactly that on the 260928-2134 build. */
const DARK_DEFAULT_MIGRATION_KEY = "pv-reader-darkdefault-v1";

export async function loadReaderSettings(): Promise<ReaderSettings> {
  const rec = await db.settings.get("reader");
  const stored = rec?.value as ReaderSettings | undefined;
  if (!stored) return defaultReaderSettings;

  // One-time migration to the dark default (2026-09-29).
  //
  // The authoritative theme lives here in Dexie, not in the `pv-theme`
  // localStorage key (which only exists so the pre-paint script can avoid a
  // flash). Changing defaultReaderSettings alone would therefore be
  // invisible to anyone who has opened the app before — i.e. everyone.
  //
  // "paper" was the previous default, so a stored "paper" is almost always
  // just that default persisted rather than a deliberate choice. Flip it
  // once, leave a marker, and never touch it again: if the user picks paper
  // on purpose afterwards it sticks. Every other stored theme was chosen
  // explicitly and is left alone.
  try {
    if (
      stored.theme === "paper" &&
      !localStorage.getItem(DARK_DEFAULT_MIGRATION_KEY)
    ) {
      localStorage.setItem(DARK_DEFAULT_MIGRATION_KEY, "1");
      const migrated: ReaderSettings = { ...stored, theme: "dark" };
      await db.settings.put({ key: "reader", value: migrated });
      return migrated;
    }
  } catch {
    /* localStorage unavailable (private mode) — skip, keep stored theme */
  }
  return stored;
}

export async function saveReaderSettings(value: ReaderSettings): Promise<void> {
  await db.settings.put({ key: "reader", value });
}

export async function loadTTSSettings(): Promise<TTSSettings> {
  const rec = await db.settings.get("tts");
  return (rec?.value as TTSSettings) ?? defaultTTSSettings;
}

export async function saveTTSSettings(value: TTSSettings): Promise<void> {
  await db.settings.put({ key: "tts", value });
  // Best-effort mirror to server-side settings bag so a fresh PWA install
  // on another device picks up the same voice / rate / provider. Only
  // the fields that actually need syncing go over the wire — theme,
  // etc, are separate reader settings and follow the same pattern.
  try {
    await patchCloudSettings({
      "tts.provider": value.provider,
      "tts.serverVoice": value.serverVoice ?? null,
      "tts.voiceURI": value.voiceURI ?? null,
      "tts.rate": value.rate,
      "tts.mode": value.mode,
    });
  } catch { /* offline, ignore */ }
}

// ── Cloud-side settings mirror ────────────────────────────────────
//
// Small settings bag on the pv-lib server so voice / speed / theme
// survive a fresh install on another device. Namespaced-key PATCH:
// { "voice.default": "af_bella" } merges into the server-side blob.
// null values delete the key. GET returns the full blob.

const LIB_SETTINGS_ENDPOINT = "/lib/settings";

function libSettingsUrl(): string {
  return remoteUrl(LIB_SETTINGS_ENDPOINT);
}

export async function fetchCloudSettings(): Promise<Record<string, unknown> | null> {
  try {
    const r = await fetch(libSettingsUrl(), { method: "GET" });
    if (!r.ok) return null;
    return (await r.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export async function patchCloudSettings(
  patch: Record<string, unknown>
): Promise<void> {
  try {
    await fetch(libSettingsUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
  } catch { /* offline, ignore */ }
}
